/**
 * Quality presets (spec §8, plan G12). `quality.json` holds the table: pixel ratio cap, bloom mode (the post
 * pipeline interprets it through `setPreset`), asteroid count (the field layout's per-preset counts must
 * agree: G5/G7), particle cap (G8's pools), and the LOD bias (every model LOD switch distance is scaled by it).
 *
 * `applyQuality` is the one place a preset change happens: post, environment (shadows) and material tier get
 * the preset, the renderer is resized at the preset's pixel ratio, the ship views build new instances with
 * the new LOD bias and existing model LODs in the scene are rescaled, then `ctx.quality` is updated and
 * `bus 'quality'` tells the features (field rebuild, particle caps, colliders). On a switch to High the engine plumes'
 * heat shimmer (High only) is warmed in the real render contexts, once per library, so the first thrust after the
 * switch builds no pipeline (PERF PF-8). Rare UI event, not hot.
 */
import { z } from 'zod'
import type { Camera, LOD, Object3D, Scene, WebGPURenderer } from 'three/webgpu'
import type { GameBus } from '../app/feature'
import { QUALITY_PRESETS, type QualityPreset } from './contracts'
import type { Environment } from './environment'
import { setMaterialPreset, type MaterialLibrary } from './materials'
import { buildShimmerWarmHolder } from './materials/engine'
import type { PostPipeline } from './post'
import qualityJson from './quality.json'
import { warmPipelines, type WarmupRenderer } from './reachFx/warmup'

const presetEnum = z.enum(QUALITY_PRESETS as readonly ['low', 'medium', 'high'])

const presetRowSchema = z.object({
  /** Pixel ratio = min(devicePixelRatio, maxPixelRatio). */
  maxPixelRatio: z.number().positive().max(4),
  /** Bloom mode the post pipeline applies for this preset (documented here; `post.setPreset` owns it). */
  bloom: z.enum(['off', 'half', 'full']),
  /** Asteroids in the field (the layout's size-bin counts sum to this). */
  asteroids: z.number().int().positive(),
  /** Total live particles across every VFX pool. */
  particleCap: z.number().int().positive(),
  /** Scale on every model LOD switch distance. */
  lodBias: z.number().positive().max(2),
})

export const qualityTableSchema = z.object({
  default: presetEnum,
  /** URL query parameter that overrides the preset for testing (`?quality=low`). */
  urlParam: z.string().min(1),
  presets: z.object({ low: presetRowSchema, medium: presetRowSchema, high: presetRowSchema }),
})

export type QualityTable = z.infer<typeof qualityTableSchema>
export type QualityRow = z.infer<typeof presetRowSchema>

export function loadQualityTable(): QualityTable {
  return qualityTableSchema.parse(qualityJson)
}

export function isQualityPreset(v: unknown): v is QualityPreset {
  return typeof v === 'string' && (QUALITY_PRESETS as readonly string[]).includes(v)
}

/** `?quality=low|medium|high` from a `location.search` string, else null. */
export function parseQualityOverride(search: string, table: QualityTable): QualityPreset | null {
  const v = new URLSearchParams(search).get(table.urlParam)
  return isQualityPreset(v) ? v : null
}

export function pixelRatioOf(table: QualityTable, preset: QualityPreset, devicePixelRatio: number): number {
  return Math.min(devicePixelRatio, table.presets[preset].maxPixelRatio)
}

/** The slice of `GameContext` a preset change touches (tests pass fakes). */
export interface QualityContext {
  readonly post: Pick<PostPipeline, 'setPreset' | 'setSize'>
  readonly env: Pick<Environment, 'setPreset'>
  readonly lib: MaterialLibrary
  /** Ship views: `lodScale` is the LOD bias for views built from now on. */
  readonly views: { lodScale: number }
  readonly scene: Scene
  /** The renderer and camera the shimmer warm-up compiles with (render/reachFx/warmup.ts). */
  readonly renderer: WebGPURenderer | WarmupRenderer
  readonly camera: Camera
  /** Loaded models by id: their manifest LOD distances are the unscaled base. */
  readonly models: ReadonlyMap<string, { readonly entry: { readonly lod: { readonly distances: readonly number[] } } }>
  readonly bus: Pick<GameBus, 'emit'>
  quality: QualityPreset
}

export interface QualityViewport {
  width: number
  height: number
  devicePixelRatio: number
}

export function browserViewport(): QualityViewport {
  return { width: window.innerWidth, height: window.innerHeight, devicePixelRatio: window.devicePixelRatio }
}

const LOD_SUFFIX = ':lod'

/**
 * Sets every model LOD under `root` (the loader names them `<asset id>:lod`) to its manifest distances × `bias`.
 * Absolute, so repeated changes never drift. Returns how many LODs were rescaled.
 */
export function rescaleModelLods(root: Object3D, models: QualityContext['models'], bias: number): number {
  let n = 0
  root.traverse((obj) => {
    if (!(obj as LOD).isLOD || !obj.name.endsWith(LOD_SUFFIX)) return
    const model = models.get(obj.name.slice(0, -LOD_SUFFIX.length))
    const levels = (obj as LOD).levels
    if (!model || model.entry.lod.distances.length !== levels.length) return
    for (let i = 0; i < levels.length; i++) levels[i].distance = model.entry.lod.distances[i] * bias
    n++
  })
  return n
}

let defaultTable: QualityTable | null = null

/** Applies `preset` everywhere (see the module comment) and emits `bus 'quality'`. */
export function applyQuality(
  ctx: QualityContext,
  preset: QualityPreset,
  viewport: QualityViewport = browserViewport(),
  table: QualityTable = (defaultTable ??= loadQualityTable()),
): void {
  const row = table.presets[preset]
  ctx.post.setPreset(preset)
  ctx.env.setPreset(preset)
  setMaterialPreset(ctx.lib, preset)
  ctx.post.setSize(viewport.width, viewport.height, pixelRatioOf(table, preset, viewport.devicePixelRatio))
  ctx.views.lodScale = row.lodBias
  ctx.quality = preset
  ctx.bus.emit('quality', preset)
  // After the emit: features that rebuild models on 'quality' get their new LODs rescaled too.
  rescaleModelLods(ctx.scene, ctx.models, row.lodBias)
  // The shimmer draws only on High, so boot warms it only when booting on High (buildPlumeWarmHolder): warm it now.
  const shimmer = buildShimmerWarmHolder(ctx.lib)
  if (shimmer !== null) void warmPipelines(ctx.renderer, ctx.scene, ctx.camera, shimmer)
}
